<template>
    <pendo-modal
        v-model:visible="active"
        width="550px"
        height="auto"
        @update:visible="active = $event"
        @closed="resetModal">
        <div
            ref="modalContent"
            @keydown.up.prevent="prev"
            @keydown.down.prevent="next"
            @keydown.enter.prevent.stop="select(selectedIdx)">
            <div class="search-container">
                <search
                    v-model="term"
                    v-focus
                    full-width
                    placeholder="Type to search..."
                    @input="handleInput" />
                <small class="help-block">
                    <kbd>&#x2191;</kbd> and <kbd>&#x2193;</kbd> to navigate, <kbd>Enter</kbd> to select.
                </small>
            </div>
            <section class="search-results">
                <pendo-loading-indicator
                    v-if="loading"
                    prevent-relative-parent
                    class="loading-indicator"
                    size="large" />
                <ul
                    v-else-if="filtered.length"
                    class="sub-list">
                    <li
                        v-for="(sub, $index) in filtered"
                        :key="sub.id"
                        class="sub"
                        :class="{
                            highlighted: isOptionHighlighted($index),
                            selected: isOptionSelected(sub),
                        }"
                        @click="select($index)">
                        <small
                            class="icon fa"
                            :class="{
                                'fa-user': sub.id == activeSub?.id || isMemberOfSub(sub.id),
                                'fa-user-plus': !isMemberOfSub(sub.id),
                                'fa-user-times': isBlocked(sub),
                            }" />
                        <span
                            v-pendo-tooltip="{ content: tooltipContent }"
                            class="sub-name"
                            @mouseover="onSubNameHover">
                            {{ sub.displayName }}
                        </span>
                        <small class="text-muted sub-text">({{ sub.name }})</small>
                    </li>
                </ul>
                <div v-else>
                    <pendo-empty-state>
                        <template #description>
                            No subscriptions were found
                        </template>
                    </pendo-empty-state>
                </div>
            </section>
        </div>
        <template #footer>
            <pendo-button
                size="mini"
                type="subtle"
                label="Reload cached subscriptions"
                prefix-icon="refresh-cw"
                :loading="isFetchingSubList"
                :disabled="isFetchingSubList"
                @click="reload" />
        </template>
    </pendo-modal>
</template>

<script>
import { PendoButton, PendoModal, PendoTooltip, PendoLoadingIndicator, PendoEmptyState } from '@pendo/components';
import Search from '@/common/search/search.vue';

import get from 'lodash/get';
import sortBy from 'lodash/sortBy';
import { mapState, mapGetters, mapActions } from 'vuex';

export default {
    name: 'ImpersonationSwitcher',
    components: {
        PendoButton,
        PendoModal,
        Search,
        PendoLoadingIndicator,
        PendoEmptyState,
    },
    directives: {
        focus: {
            inserted (el) {
                el.focus();
            },
        },
        PendoTooltip,
    },
    data () {
        return {
            active: false,
            selectedIdx: 0,
            term: '',
            tooltipContent: null,
        };
    },
    computed: {
        ...mapState({
            activeSub: (state) => state.subscription.active,
            isFetchingSubList: (state) => state.subscription.super.fetching,
            subMap: (state) => state.subscription.map,
        }),
        ...mapGetters({
            fullSubList: 'subscription/super/list',
            hasSuperAccess: 'auth/hasSuperAccess',
        }),
        sorted () {
            return sortBy(this.fullSubList, [
                (sub) => {
                    const isMemberOfSub = this.isMemberOfSub(sub.id);

                    // always display the active sub first
                    if (sub.id === this.activeSub.id) return -1;

                    // make {memberships} appear ahead of other {subscriptions}
                    return isMemberOfSub ? 0 : 1;
                },
                'displayName',
                'name',
            ]);
        },
        filtered () {
            if (!this.term) return this.sorted.slice(0, 1000);

            return this.sorted
                .filter((sub) => {
                    return ['displayName', 'name'].some((key) => {
                        const [value, term] = [sub[key], this.term].map((value) => value.toLowerCase());

                        return value.includes(term);
                    });
                })
                .slice(0, 1000);
        },
        loading () {
            return this.isFetchingSubList && !this.fullSubList?.length;
        },
    },
    created () {
        if (this.hasSuperAccess) {
            this.addKeypressListener();
        }
    },
    beforeUnmount () {
        window.removeEventListener('keydown', this.toggleSwitcher);
    },
    methods: {
        ...mapActions({
            loadAllSubscriptions: 'subscription/super/loadAll',
        }),
        isMemberOfSub (subId) {
            return !!this.subMap[subId];
        },
        addKeypressListener () {
            window.addEventListener('keydown', this.toggleSwitcher);
        },
        toggleSwitcher ($event) {
            if ($event.metaKey && $event.code === 'Backslash') {
                $event.preventDefault();
                this.active = true;
                this.loadAllSubscriptions();
            }
        },
        scrollTo (index) {
            const listItems = this.$refs.modalContent?.querySelectorAll('li.sub');
            if (listItems?.[index]) {
                listItems[index].scrollIntoView({ block: 'nearest' });
            }
        },
        prev () {
            this.selectedIdx = this.selectedIdx > 0 ? this.selectedIdx - 1 : this.selectedIdx;
            this.scrollTo(this.selectedIdx);
        },
        next () {
            this.selectedIdx =
                this.selectedIdx >= this.filtered.length - 1 ? this.filtered.length - 1 : this.selectedIdx + 1;
            this.scrollTo(this.selectedIdx);
        },
        isBlocked (sub) {
            return get(sub, 'featureFlags.blockSuperDataAccess', false);
        },
        select (index) {
            const subscriptionId = get(this.filtered, [index, 'id']);

            if (subscriptionId) {
                this.$router.pendo.switchSubscription(subscriptionId);
            }
        },
        reload () {
            this.term = '';
            this.selectedIdx = 0;
            this.loadAllSubscriptions({ reFetch: true });
        },
        onSubNameHover ({ currentTarget }) {
            const { clientWidth, scrollWidth } = currentTarget;
            this.tooltipContent = clientWidth < scrollWidth ? currentTarget.textContent : null;
        },
        resetModal () {
            this.term = '';
            this.selectedIdx = 0;
        },
        handleInput () {
            this.$nextTick(() => {
                if (this.selectedIdx > this.filtered.length - 1) {
                    this.selectedIdx = this.filtered.length - 1;
                }
            });
        },
        isOptionHighlighted (index) {
            return index === this.selectedIdx;
        },
        isOptionSelected (sub) {
            return sub.id === this.activeSub?.id;
        },
    },
};
</script>

<style lang="less" scoped>
.modal-content {
    outline: none;
}

:deep(.pendo-modal__header) {
    display: none;
}

:deep(.pendo-modal__body) {
    padding: 0px;
}

:deep(.pendo-modal__footer) {
    min-height: unset;
}

.search-container {
    padding: 16px 16px 0px 16px;
}

.help-block {
    margin: 10px 0;
}

.search-results {
    position: relative;
    min-height: 400px;

    .loading-indicator {
        height: 400px;
        display: flex;
        align-items: center;
        justify-content: center;
    }
}

.sub-list {
    max-height: 400px;
    overflow-y: auto;
    list-style: none;
    width: 100%;
    margin: 0px;
    padding: 0px 16px;

    .sub {
        font-size: 14px;
        padding: 4px 8px;
        margin-top: 2px;
        display: flex;
        align-items: center;
        gap: 8px;
        border-radius: 3px;
        max-width: 100%;
        overflow: hidden;

        &:first-of-type {
            margin-top: 0;
        }

        .icon {
            color: var(--palette-gray-60);
            flex-shrink: 0;
        }

        .sub-name {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }

        .sub-text {
            font-size: 12px;
            white-space: nowrap;
        }

        &.highlighted {
            background-color: var(--palette-gray-40);
        }

        &.selected .icon {
            color: var(--palette-pink-120);
        }

        &:hover {
            background-color: var(--palette-gray-30);
            cursor: pointer;
        }
    }
}
</style>
